@if (isOpen) {
    <div
        class="absolute right-0 top-[calc(100%+8px)] z-50 w-[300px] origin-top-right overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-[0_18px_48px_-18px_rgba(15,23,42,0.18),0_2px_8px_-3px_rgba(15,23,42,0.08)] animate-rise-fade"
        tabindex="-1"
        (click)="$event.stopPropagation()"
        (keydown.escape)="close.emit()">
        <!-- Identity header -->
        <div class="flex items-center gap-3 px-3.5 py-3">
            <tw-user-avatar
                class="ring-1 ring-zinc-200/80"
                size="md"
                [name]="name()"
                [src]="profileImg()" />
            <div class="min-w-0 flex-1">
                <p
                    class="truncate text-[13.5px] font-semibold leading-tight tracking-[-0.005em] text-zinc-900">
                    {{ name() }}
                </p>
                <p class="mt-0.5 truncate text-[11.5px] leading-tight text-zinc-500">
                    {{ email() }}
                </p>
            </div>
        </div>

        <div class="mx-3.5 h-px bg-zinc-200/70"></div>

        <!-- CONSUMO — MACs used vs contracted, mirrored from the dashboard usage display -->
        <div class="px-3.5 pb-3 pt-3">
            <div class="flex items-center justify-between">
                <span
                    class="text-[10.5px] font-medium uppercase tracking-[0.14em] text-zinc-500">
                    {{ T.consumo | translate }}
                </span>
                @if (hasCustomPricing() && isSubscribed()) {
                    <button
                        type="button"
                        class="inline-flex h-7 items-center gap-1.5 rounded-lg border border-zinc-200/80 bg-white px-2.5 text-[12px] font-semibold tracking-[-0.005em] text-zinc-700 transition-colors duration-200 ease-out-expo hover:border-zinc-300 hover:text-zinc-900"
                        [title]="T.contactSales | translate"
                        (click)="handleContactSales()">
                        <lucide-icon [img]="HeadsetIcon" [size]="13" [strokeWidth]="1.75" />
                        {{ T.contactSales | translate }}
                    </button>
                } @else {
                    <button
                        type="button"
                        class="inline-flex h-7 items-center justify-center rounded-lg bg-zinc-900 px-3 text-[12px] font-semibold tracking-[-0.005em] text-white shadow-ctaPrimary transition-[transform,background-color] duration-200 ease-out-expo hover:-translate-y-px hover:bg-zinc-800"
                        (click)="handleUpgrade()">
                        {{ T.upgrade | translate }}
                    </button>
                }
            </div>

            <dl class="mt-2.5 space-y-1.5">
                <div class="flex items-baseline justify-between">
                    <dt class="text-[12.5px] text-zinc-500">{{ T.used | translate }}</dt>
                    <dd
                        class="font-mono tabular-nums text-[12.5px] font-semibold text-zinc-900">
                        {{ macsConsumed() | number }}
                    </dd>
                </div>
                <div class="flex items-baseline justify-between">
                    <dt class="text-[12.5px] text-zinc-500">{{ T.contracted | translate }}</dt>
                    <dd
                        class="font-mono tabular-nums text-[12.5px] font-medium text-zinc-900">
                        {{ macsTotal() | number }}
                        <span class="ml-1 text-[11px] font-normal text-zinc-400">
                            {{ T.macsSuffix | translate }}
                        </span>
                    </dd>
                </div>
            </dl>

            <!-- Usage bar -->
            <div
                class="relative mt-2.5 h-1.5 overflow-hidden rounded-full bg-zinc-100 ring-1 ring-inset ring-zinc-200/60">
                <div
                    class="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500 ease-out-expo"
                    [class]="usagePct() >= 0.8 ? 'bg-red-500' : 'bg-zinc-900'"
                    [style.width.%]="usagePct() * 100"></div>
            </div>
        </div>

        <div class="mx-3.5 h-px bg-zinc-200/70"></div>

        <!-- WORKSPACE -->
        <div class="px-3.5 py-3">
            <span class="text-[10.5px] font-medium uppercase tracking-[0.14em] text-zinc-500">
                {{ T.currentWorkspace | translate }}
            </span>
            <div class="mt-1.5 flex items-center justify-between gap-2">
                <div class="min-w-0">
                    <p
                        class="truncate text-[13.5px] font-semibold leading-tight tracking-[-0.005em] text-zinc-900">
                        {{ accountName() || ('—') }}
                    </p>
                    @if (planLabel(); as plan) {
                        <p class="mt-0.5 truncate text-[11.5px] leading-tight text-zinc-500">
                            {{ plan | titlecase }}
                        </p>
                    }
                </div>
                <button
                    type="button"
                    [attr.aria-label]="T.switchWorkspace | translate"
                    [title]="T.switchWorkspace | translate"
                    class="flex size-7 shrink-0 items-center justify-center rounded-lg border border-zinc-200/80 bg-white text-zinc-500 transition-colors duration-200 ease-out-expo hover:border-zinc-300 hover:text-zinc-900"
                    (click)="$event.stopPropagation()">
                    <lucide-icon [img]="SwitchIcon" [size]="13" [strokeWidth]="1.75" />
                </button>
            </div>
        </div>

        <div class="mx-3.5 h-px bg-zinc-200/70"></div>

        <!-- ENTRIES -->
        <div class="py-1.5">
            <button
                type="button"
                class="group flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] font-medium text-zinc-600 transition-colors duration-150 ease-out-expo hover:bg-zinc-50 hover:text-zinc-900"
                (click)="handleSettings()">
                <span
                    class="flex size-7 items-center justify-center rounded-lg bg-zinc-100/70 text-zinc-500 transition-colors duration-150 group-hover:bg-primary-50 group-hover:text-primary-600">
                    <lucide-icon [img]="SettingsIcon" [size]="14" [strokeWidth]="1.75" />
                </span>
                <span>{{ T.settings | translate }}</span>
            </button>

            @if (hasApiAccess()) {
                <button
                    type="button"
                    class="group flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] font-medium text-zinc-600 transition-colors duration-150 ease-out-expo hover:bg-zinc-50 hover:text-zinc-900"
                    (click)="handleApiKeys()">
                    <span
                        class="flex size-7 items-center justify-center rounded-lg bg-zinc-100/70 text-zinc-500 transition-colors duration-150 group-hover:bg-primary-50 group-hover:text-primary-600">
                        <lucide-icon [img]="ApiKeysIcon" [size]="14" [strokeWidth]="1.75" />
                    </span>
                    <span>{{ T.apiKeys | translate }}</span>
                </button>

                <button
                    type="button"
                    class="group flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] font-medium text-zinc-600 transition-colors duration-150 ease-out-expo hover:bg-zinc-50 hover:text-zinc-900"
                    (click)="handleWebhooks()">
                    <span
                        class="flex size-7 items-center justify-center rounded-lg bg-zinc-100/70 text-zinc-500 transition-colors duration-150 group-hover:bg-primary-50 group-hover:text-primary-600">
                        <lucide-icon [img]="WebhooksIcon" [size]="14" [strokeWidth]="1.75" />
                    </span>
                    <span>{{ T.webhooks | translate }}</span>
                </button>
            }

            <button
                type="button"
                class="group flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] font-medium text-zinc-600 transition-colors duration-150 ease-out-expo hover:bg-zinc-50 hover:text-zinc-900"
                (click)="handleLogout()">
                <span
                    class="flex size-7 items-center justify-center rounded-lg bg-zinc-100/70 text-zinc-500 transition-colors duration-150 group-hover:bg-red-50 group-hover:text-red-600">
                    <lucide-icon [img]="LogoutIcon" [size]="14" [strokeWidth]="1.75" />
                </span>
                <span>{{ C.logout | translate }}</span>
            </button>
        </div>
    </div>
}
